import React, { useMemo, useEffect, useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { Row, Col, Card, Form, Button, message } from 'antd';
import html2canvas from 'html2canvas';
import OdontogramChart from './odontogram/OdontogramChart';
import OdontogramMenu from './odontogram/OdontogramMenu';
import ClinicalSummary from './odontogram/ClinicalSummary';
import NormalForm from './NormalForm';
import useOdontogram from '../../hooks/useOdontogram';
import { toothColors, resolveConditionColor, findingTypes, toothOrderRows, dentalInputCodes, permanent28 } from './odontogram/odontogramConfig';
import { computeOdontogramCounts } from './odontogram/odontogramUtils';
import proceduresCodes from '../../../../utils/proceduresCodes.json';

const clinicalSummaryCodes = new Set([
  dentalInputCodes.dentalStatus,
  dentalInputCodes.cpodC, dentalInputCodes.cpodP, dentalInputCodes.cpodO, dentalInputCodes.cpodTotal,
  dentalInputCodes.softPlaque, dentalInputCodes.calcifiedPlaque, dentalInputCodes.gingivalLegacy,
  dentalInputCodes.gingivalValue, dentalInputCodes.gingivalInterpretation,
  dentalInputCodes.cariesCount, dentalInputCodes.missingCount, dentalInputCodes.remanentRootCount,
  dentalInputCodes.necrosisCount, dentalInputCodes.badStateCount,
  dentalInputCodes.cariesList, dentalInputCodes.missingList, dentalInputCodes.remanentRootList,
  dentalInputCodes.necrosisList, dentalInputCodes.badStateList,
]);

const OdontogramForm = forwardRef(({
  inputs,
  disableForm,
  form,
  roles,
  fileList,
  loadingFiles,
  status,
  setFileList,
  toDoctor,
  removeDocument,
  convertFile,
  convertError,
  isDICOM,
  handleConvertFile,
  imageUrl,
  setImageUrl,
  countrySettings,
  odontogramState,
  setOdontogramState,
  fromExternal,
}, ref) => {
  const {
    toothFindings,
    multiToothFindings,
    cpodExcludedTeeth,
    activeFinding,
    selectFinding,
    addFinding,
    addMultiFinding,
    removeMultiFinding,
    removeFinding,
    clearAllFindings,
    toggleCpodExclusion,
    setOdontogramData,
  } = useOdontogram(odontogramState);
  const [activeColor, setActiveColor] = useState(toothColors.healthy);
  const [multiToothStart, setMultiToothStart] = useState(null);
  const [gridEdits, setGridEdits] = useState({});
  const [clinicalSummaryValues, setClinicalSummaryValues] = useState(() => {
    const seed = {};
    clinicalSummaryCodes.forEach((code) => {
      const procedure = inputs.procedures?.find((item) => item.code === code);
      if (procedure?.result) {
        try { seed[code] = JSON.parse(procedure.result); } catch { seed[code] = procedure.result; }
      } else if ([dentalInputCodes.softPlaque, dentalInputCodes.calcifiedPlaque].includes(code)) {
        seed[code] = 'no';
      }
    });
    return seed;
  });

  const odontogramStateIdRef = useRef(odontogramState);


  useEffect(() => {
    setOdontogramState({ toothFindings, multiToothFindings, cpodExcludedTeeth });
  }, [toothFindings, multiToothFindings, cpodExcludedTeeth, setOdontogramState]);

  useEffect(() => {
    const proceduresUpdate = { ...(form.getFieldValue('procedures') || {}) };
    clinicalSummaryCodes.forEach((code) => {
      const procedure = inputs.procedures?.find((item) => item.code === code);
      const value = clinicalSummaryValues[code];
      if (procedure && value !== undefined) {
        proceduresUpdate[procedure.id] = value;
      }
    });
    form.setFieldsValue({ procedures: proceduresUpdate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (odontogramState && odontogramState !== odontogramStateIdRef.current) {
      odontogramStateIdRef.current = odontogramState;
      setOdontogramData(odontogramState);
    }
  }, [odontogramState, setOdontogramData]);

  useEffect(() => {
    setMultiToothStart(null);
  }, [activeFinding?.code]);

  useEffect(() => {
    const { cpodC, cpodP, cpodO, cpodTotal, caries, missing, remanentRoot, necrosis, badState } =
      computeOdontogramCounts(toothFindings, multiToothFindings, cpodExcludedTeeth);
    const updates = {
      [dentalInputCodes.cpodC]: cpodC,
      [dentalInputCodes.cpodP]: cpodP,
      [dentalInputCodes.cpodO]: cpodO,
      [dentalInputCodes.cpodTotal]: cpodTotal,
      [dentalInputCodes.cariesCount]: caries.count,
      [dentalInputCodes.missingCount]: missing.count,
      [dentalInputCodes.remanentRootCount]: remanentRoot.count,
      [dentalInputCodes.necrosisCount]: necrosis.count,
      [dentalInputCodes.badStateCount]: badState.count,
      [dentalInputCodes.cariesList]: caries.teeth.join(','),
      [dentalInputCodes.missingList]: missing.teeth.join(','),
      [dentalInputCodes.remanentRootList]: remanentRoot.teeth.join(','),
      [dentalInputCodes.necrosisList]: necrosis.teeth.join(','),
      [dentalInputCodes.badStateList]: badState.teeth.join(','),
    };

    setClinicalSummaryValues(prev => {
      const next = { ...prev };
      Object.entries(updates).forEach(([code, value]) => { next[code] = value; });
      return next;
    });
    const proceduresUpdate = { ...(form.getFieldValue('procedures') || {}) };
    inputs.procedures?.forEach((procedure) => {
      if (updates[procedure.code] !== undefined) {
        proceduresUpdate[procedure.id.toString()] = updates[procedure.code];
      }
    });
    form.setFieldsValue({ procedures: proceduresUpdate });
  }, [toothFindings, multiToothFindings, cpodExcludedTeeth, inputs.procedures, form]);

  useImperativeHandle(ref, () => ({
    captureNormativeImage: async () => {
      const element = document.getElementById('odontogram-capture-area');
      if (!element) return null;
      try {
        const canvas = await html2canvas(element, {
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false,
          useCORS: true,
          width: element.scrollWidth,
          height: element.scrollHeight,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
          scrollX: 0,
          scrollY: 0,
          onclone: (clonedDoc) => {
            const clonedElement = clonedDoc.getElementById('odontogram-capture-area');
            if (clonedElement) {
              clonedElement.style.width = `${element.scrollWidth}px`;
              clonedElement.style.height = `${element.scrollHeight}px`;
              clonedElement.style.overflow = 'visible';
            }
          },
        });
        return canvas.toDataURL('image/png');
      } catch (error) {
        console.error('[OdontogramForm] captureNormativeImage failed:', error);
        message.error({
          content: `Error al capturar la imagen del odontograma.`,
          style: {
            marginTop: '40vh',
          },
        });
        return null;
      }
    },
  }));

  const normalFormInputs = useMemo(() => ({
    ...inputs,
    procedures: inputs.procedures?.filter(
      (procedure) =>
        procedure.code !== proceduresCodes.dental.chart
        && !clinicalSummaryCodes.has(procedure.code)
    ),
  }), [inputs]);

  const getDrawColor = useCallback((finding) => resolveConditionColor(finding, activeColor), [activeColor]);

  const handleClinicalSummaryChange = useCallback((code, value) => {
    setClinicalSummaryValues((prev) => ({ ...prev, [code]: value }));
    const procedure = inputs.procedures?.find((item) => item.code === code);
    if (procedure) {
      form.setFieldsValue({
        procedures: { ...form.getFieldValue('procedures'), [procedure.id]: value },
      });
    }
  }, [inputs.procedures, form]);

  const handleGridEdit = useCallback((editKey, text, color) => {
    setGridEdits(prev => ({ ...prev, [editKey]: { text, color } }));
  }, []);

  const handleWholeToothFinding = useCallback((toothNumber) => {
    if (disableForm || !activeFinding) return;

    if (activeFinding.eraser === 'cpodExclude') {
      const tooth = Number(toothNumber);
      if (!permanent28.includes(tooth)) {
        message.warning('Solo piezas permanentes pueden excluirse del CPOD.');
        return;
      }
      const wasExcluded = cpodExcludedTeeth.includes(tooth);
      toggleCpodExclusion(tooth);
      message.info(wasExcluded
        ? `Pieza ${tooth} incluida en el índice CPOD`
        : `Pieza ${tooth} excluida del índice CPOD`);
      return;
    }

    const configDef = findingTypes[activeFinding.code];

    if (!activeFinding.eraser && configDef?.multiTooth) {
      if (!multiToothStart) {
        setMultiToothStart(toothNumber);
        message.info(`Pieza ${toothNumber} seleccionada. Haga clic en la segunda pieza.`);
        return;
      }
      if (configDef?.requiresAdjacency) {
        const from = parseInt(multiToothStart, 10);
        const to = parseInt(toothNumber, 10);
        let adjacent = false;
        for (const row of toothOrderRows) {
          const i1 = row.indexOf(from);
          const i2 = row.indexOf(to);
          if (i1 !== -1 && i2 !== -1 && Math.abs(i1 - i2) === 1) { adjacent = true; break; }
        }
        if (!adjacent) {
          message.warning(`El registro de "${activeFinding.label}" solo se permite entre piezas adyacentes.`);
          setMultiToothStart(null);
          return;
        }
      }
      addMultiFinding(
        { ...activeFinding, color: getDrawColor(activeFinding) },
        multiToothStart,
        toothNumber,
      );
      setMultiToothStart(null);
      return;
    }

    if (!activeFinding.eraser && configDef?.appliesToWholeTooth === false) {
      return;
    }
    addFinding(toothNumber, null, { ...activeFinding, color: getDrawColor(activeFinding) });
  }, [disableForm, activeFinding, multiToothStart, addFinding, addMultiFinding, getDrawColor, cpodExcludedTeeth, toggleCpodExclusion]);

  const handleSurfaceClick = useCallback((toothNumber, surface) => {
    if (disableForm || !activeFinding) return;

    if (activeFinding.eraser === 'cpodExclude') {
      handleWholeToothFinding(toothNumber);
      return;
    }

    const isEraser = activeFinding.eraser;
    const configDef = findingTypes[activeFinding.code];

    if (!isEraser && configDef?.multiTooth) {
      handleWholeToothFinding(toothNumber);
      return;
    }

    let appliedSurface;
    if (isEraser) {
      appliedSurface = surface;
    } else if (configDef?.appliesToWholeTooth) {
      appliedSurface = null;
    } else {
      appliedSurface = surface;
    }
    addFinding(toothNumber, appliedSurface, { ...activeFinding, color: getDrawColor(activeFinding) });
  }, [disableForm, activeFinding, addFinding, handleWholeToothFinding, getDrawColor]);

  return (
    <div className="odontogram-form-container">
      <div className={`odontogram-layout${fromExternal ? ' odontogram-layout--compact' : ''}`}>
        <div className="odontogram-layout__menu">
          <OdontogramMenu
            activeFinding={activeFinding}
            onSelectFinding={selectFinding}
            activeColor={activeColor}
            onSelectColor={setActiveColor}
            disabled={disableForm}
          />
        </div>
        <div className="odontogram-layout__chart">
          <Card
            bordered={false}
            className="odontogram-chart-card"
          >
            {multiToothStart && (
              <div className="odontogram-multi-tooth-banner">
                <span>
                  <strong>{activeFinding?.label}</strong> — Pieza {multiToothStart} seleccionada.
                  Haga clic en la segunda pieza.
                </span>
                <Button size="small" type="link" onClick={() => setMultiToothStart(null)}>Cancelar</Button>
              </div>
            )}
            <OdontogramChart
              toothFindings={toothFindings}
              multiToothFindings={multiToothFindings}
              onSurfaceClick={handleSurfaceClick}
              onApplyFinding={handleWholeToothFinding}
              onAddMultiFinding={addMultiFinding}
              activeColor={activeColor}
              gridEdits={gridEdits}
              onGridEdit={handleGridEdit}
              disabled={disableForm}
            />
          </Card>

          {fromExternal && (
            <div className="odontogram-layout__summary--inline">
              <ClinicalSummary
                toothFindings={toothFindings}
                multiToothFindings={multiToothFindings}
                formValues={clinicalSummaryValues}
                onChange={handleClinicalSummaryChange}
                onRemoveFinding={removeFinding}
                onRemoveMultiFinding={removeMultiFinding}
                onClearAll={clearAllFindings}
                disabled={disableForm}
                proceduresCodes={dentalInputCodes}
                cpodExcludedTeeth={cpodExcludedTeeth}
                onToggleCpodExclusion={toggleCpodExclusion}
                compact
              />
            </div>
          )}
        </div>

        {!fromExternal && (
          <div className="odontogram-layout__summary">
            <ClinicalSummary
              toothFindings={toothFindings}
              multiToothFindings={multiToothFindings}
              formValues={clinicalSummaryValues}
              onChange={handleClinicalSummaryChange}
              onRemoveFinding={removeFinding}
              onRemoveMultiFinding={removeMultiFinding}
              onClearAll={clearAllFindings}
              disabled={disableForm}
              proceduresCodes={dentalInputCodes}
              cpodExcludedTeeth={cpodExcludedTeeth}
              onToggleCpodExclusion={toggleCpodExclusion}
            />
          </div>
        )}
      </div>

      <Row justify="center" className="odontogram-normal-form-row">
        <Col lg={20} xs={24}>
          <NormalForm
            form={form}
            inputs={normalFormInputs}
            disableForm={disableForm}
            roles={roles}
            status={status}
            loadingFiles={loadingFiles}
            setFileList={setFileList}
            fileList={fileList}
            toDoctor={toDoctor}
            removeDocument={removeDocument}
            convertFile={convertFile}
            convertError={convertError}
            isDICOM={isDICOM}
            handleConvertFile={handleConvertFile}
            imageUrl={imageUrl}
            setImageUrl={setImageUrl}
            countrySettings={countrySettings}
          />
        </Col>
      </Row>

      {inputs.procedures
        ?.filter(procedure => clinicalSummaryCodes.has(procedure.code))
        .map(procedure => (
          <Form.Item key={procedure.id} name={['procedures', procedure.id.toString()]} noStyle>
            <input type="hidden" />
          </Form.Item>
        ))
      }
    </div>
  );
});

export default OdontogramForm;
